import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  UsersFour, MagnifyingGlass, CheckCircle, User, ShieldCheck, Warning, DeviceMobile,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SignaturePad from "@/components/SignaturePad";
import SelfieCapture from "@/components/SelfieCapture";

/**
 * KioskMobile — pantalla accedida por el móvil personal del trabajador
 * al escanear el QR mostrado en el tablet del supervisor. NO requiere
 * cuenta ni contraseña: valida el token efímero de la sesión kiosco y
 * permite firmar la asistencia del día una sola vez.
 *
 * Regla de selfie: obligatorio en el primer turno del mes; después opcional.
 */
export default function KioskMobile() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const base = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [signature, setSignature] = useState("");
  const [selfie, setSelfie] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [selfieMeta, setSelfieMeta] = useState({ required: false, reason: "" });

  useEffect(() => {
    if (!token) { setError("Token faltante en el QR."); setLoading(false); return; }
    axios.get(`${base}/api/hr/kiosk/sessions/${token}`)
      .then((r) => { setSession(r.data); setLoading(false); })
      .catch((e) => {
        setError(e?.response?.data?.detail || "QR inválido o expirado.");
        setLoading(false);
      });
  }, [base, token]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !session) return [];
    return (session.employees || []).filter((e) =>
      (e.name || "").toLowerCase().includes(s)
      || (e.dni || "").toLowerCase().includes(s)
      || (e.crew || "").toLowerCase().includes(s)
    ).slice(0, 8);
  }, [session, q]);

  const pickEmp = useCallback(async (emp) => {
    setPicked(emp);
    try {
      const r = await axios.get(`${base}/api/hr/employees/${emp.id}/selfie-required`, {
        // este endpoint requiere JWT normalmente; en modo QR el trabajador
        // no está loggeado. Si falla, asumimos "requerido" para ser seguros.
      });
      setSelfieMeta({ required: !!r.data?.required, reason: r.data?.reason || "" });
    } catch {
      // Sin sesión JWT no podemos leerlo — el backend igual valida al firmar.
      setSelfieMeta({ required: true, reason: "requerido en primera firma del mes" });
    }
  }, [base]);

  const sign = useCallback(async () => {
    if (!picked || !signature) { toast.error("Elige tu nombre y dibuja tu firma"); return; }
    if (selfieMeta.required && !selfie) {
      toast.error("Selfie obligatorio en la primera firma del mes"); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${base}/api/hr/kiosk/sessions/qr-sign`, {
        token, employee_id: picked.id,
        signature_data_url: signature,
        selfie_data_url: selfie,
      });
      setDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al firmar");
    } finally { setBusy(false); }
  }, [base, picked, signature, selfie, token, selfieMeta.required]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <p className="text-slate-400">Validando QR…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="bg-slate-900 border border-red-800 rounded-2xl p-8 text-center max-w-md">
          <Warning size={44} weight="duotone" className="text-red-400 mx-auto mb-3" />
          <h1 className="font-heading font-black text-xl mb-2">QR inválido</h1>
          <p className="text-sm text-slate-300">{error}</p>
          <p className="text-[11px] text-slate-500 mt-3">Pide al supervisor un QR nuevo desde el modo Kiosco.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-950 text-white flex items-center justify-center p-6">
        <div className="bg-slate-900 border border-emerald-700 rounded-2xl p-8 text-center max-w-md" data-testid="kiosk-mobile-done">
          <CheckCircle size={54} weight="fill" className="text-emerald-400 mx-auto mb-3" />
          <h1 className="font-heading font-black text-2xl mb-2">¡Firmado!</h1>
          <p className="text-slate-200">Gracias {done.employee_name}. Tu asistencia del {done.date} quedó registrada.</p>
          <p className="text-[10px] font-mono text-slate-500 mt-3 break-all">Hash: {done.audit_hash?.slice(0, 24)}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white p-4" data-testid="kiosk-mobile-page">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="flex items-center gap-3 pt-2">
          <div className="h-11 w-11 rounded-lg bg-blue-800 flex items-center justify-center">
            <DeviceMobile size={24} weight="fill" className="text-amber-300" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-blue-200">Firma desde tu celular</p>
            <h1 className="font-heading font-black text-xl leading-tight">Modo Kiosco QR</h1>
            <p className="text-[11px] text-slate-400">Supervisor: <b className="text-slate-200">{session?.operator || "-"}</b></p>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl">
          {!picked ? (
            <>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2 block">
                Busca tu nombre o DNI
              </label>
              <div className="relative">
                <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ej: Juan Pérez, 12345678"
                  className="pl-10 h-12 text-base bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="kiosk-mobile-search"
                />
              </div>
              {filtered.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="kiosk-mobile-results">
                  {filtered.map((e) => (
                    <li key={e.id}>
                      <button
                        onClick={() => pickEmp(e)}
                        data-testid={`kiosk-mobile-pick-${e.id}`}
                        className="w-full text-left px-3 py-3 rounded-lg bg-slate-800 hover:bg-blue-800 border border-slate-700 hover:border-blue-500 transition"
                      >
                        <div className="flex items-center gap-3">
                          <User size={18} weight="fill" className="text-slate-400" />
                          <div className="flex-1">
                            <p className="font-bold text-white text-sm">{e.name}</p>
                            <p className="text-[10px] text-slate-400">{e.position} · Cuadr. {e.crew || "-"} · DNI {e.dni || "-"}</p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {q && filtered.length === 0 && (
                <p className="mt-3 text-sm text-slate-500 italic text-center">Sin coincidencias.</p>
              )}
            </>
          ) : (
            <>
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Firmando como</p>
                <p className="font-heading font-black text-lg" data-testid="kiosk-mobile-picked">{picked.name}</p>
                <p className="text-[11px] text-slate-400">{picked.position} · Cuadrilla {picked.crew || "-"}</p>
              </div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1 block">
                Dibuja tu firma
              </label>
              <div className="bg-white p-2 rounded-lg">
                <SignaturePad value={signature} onChange={setSignature} width={420} height={150} testid="kiosk-mobile-sig" />
              </div>
              <div className="mt-3">
                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1 block">
                  Selfie {selfieMeta.required ? <span className="text-red-400">(obligatorio · {selfieMeta.reason})</span> : "(opcional)"}
                </label>
                <SelfieCapture value={selfie} onChange={setSelfie} testid="kiosk-mobile-selfie" />
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline" onClick={() => { setPicked(null); setSignature(""); setSelfie(""); }}
                  className="flex-1 bg-transparent border-slate-700 text-white hover:bg-slate-800"
                  data-testid="kiosk-mobile-back"
                >
                  Cambiar
                </Button>
                <Button
                  onClick={sign} disabled={busy || !signature || (selfieMeta.required && !selfie)}
                  data-testid="kiosk-mobile-confirm"
                  className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black gap-1"
                >
                  <CheckCircle size={18} weight="fill" /> {busy ? "Firmando…" : "Confirmar"}
                </Button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-[10px] text-slate-500 flex items-center justify-center gap-1">
          <ShieldCheck size={12} weight="fill" className="text-emerald-500" />
          Sesión válida hasta {session?.expires_at?.slice(0, 16).replace("T", " ")} UTC · {session?.signings || 0} firmas
        </p>
      </div>
    </div>
  );
}
